var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from 'i18next';
import { getPublicApi } from '../../utils/fetcher';
import './PublicFeedback';
import { styleMap } from 'lit-html/directives/style-map.js';
import { animate, fade, flyBelow } from '@lit-labs/motion';
import { BaseComponent } from '../../utils/BaseComponent';
import { trackEvent } from '../../utils/trackPulse';
import { SettingsController } from '../../utils/SettingsController';
import '../../components/Icon';
let PublicListing = class PublicListing extends BaseComponent {
    constructor() {
        super(...arguments);
        this.columns = 1;
        this.nrOfFeedbacks = 6;
        this.noShadows = false;
        this.showReplies = false;
        this.expandable = false;
        this.showTransactionalBadge = false;
        this._settingListener = new SettingsController(this, this.getData.bind(this));
        this.expanded = false;
        this.animation = false;
    }
    connectedCallback() {
        super.connectedCallback();
        if (!this._tracked) {
            trackEvent({
                name: 'View trust public listing',
                intent: 'Show',
                type: 'View',
                position: this.position,
            }, 'View public listing', 'Trust public listing', this.vertical);
        }
        this._tracked = true;
    }
    willUpdate(_changedProperties) {
        if (_changedProperties.has('user')) {
            this.getData();
        }
    }
    async getData() {
        if (window.trustSettings) {
            const url = `/public/users/${this.user}/receivedFeedback`;
            const { data: publicListingParticipants, error } = await getPublicApi(url, window.trustSettings);
            if (!error) {
                this.participants = publicListingParticipants.sort((a, b) => new Date(b.feedback.givenAt).getTime() - new Date(a.feedback.givenAt).getTime());
            }
            else {
                this._error = {};
            }
        }
    }
    render() {
        var _a, _b, _c, _d, _e;
        if (!window.trustSettings)
            return nothing;
        if (this._error)
            return nothing;
        if (!(((_a = this.participants) === null || _a === void 0 ? void 0 : _a.length) > 0)) {
            return html `
        <h3>
            ${t('Feedbacks')}
            <span class="text-secondary text-16">
              (0)
            </span>
        </h3>
        ${t('No feeedbacks')}
      `;
        }
        const filteredParticipants = this.expandable && this.expanded ? this.participants : (_b = this.participants) === null || _b === void 0 ? void 0 : _b.slice(0, this.nrOfFeedbacks);
        const publicListingFeedback = (participant, i) => {
            const personProfileUrl = this.profileUrl && participant ? this.profileUrl.replace('{{userId}}', participant.localProfileId) : undefined;
            return html `
        <trust-public-listing-feedback
          ?noShadows=${this.noShadows}
          ${animate({
                keyframeOptions: {
                    delay: this.animation ? 0 : ((this.expanded ? i - this.nrOfFeedbacks : i) * 500) / filteredParticipants.length,
                    duration: 500,
                    fill: 'both',
                },
                onComplete: () => this.animation = this.expanded,
                properties: [],
                in: flyBelow,
                out: fade,
                skipInitial: true
            })}
          .participant='${participant}'
          ?showReplies=${this.showReplies}
          ?showTransactionalBadge=${this.showTransactionalBadge}
          profileUrl='${personProfileUrl}'
        ></trust-public-listing-feedback>
    `;
        };
        const showMoreButton = html `
      <div class='mt-8 flex gap-16' data-test=${!this.expanded ? 'canExpand' : 'isExpanded'}>
        <button tabindex='0'
                class='border-none bg-none text-sm text-link hover:brightness-75 hover:underline cursor-pointer flex items-center'
                data-test="public-listing-toggle"
                @click='${this.toggleExpandable}'
        >
          <span class="p strong mr-8 text-link">${this.expanded ? t('Close') : t('Expand')}</span>
          <trust-icon name="ChevronDown" classNames="w-16 h-16 fill-primary ${this.expanded ? 'rotate-180' : ''} hover:scale-125 trasisition-all duration-200"></trust-icon>
        </button>
      </div>
    `;
        const header = html `
      <h3>
          ${t('Feedbacks')}
          <span class="text-secondary text-16">
            (${(_d = (_c = this.participants) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0})
        </span>
      </h3>
      `;
        return html `
      ${header}
      <ul
        class='grid gap-16 list-none p-0'
        style='${styleMap({ 'grid-template-columns': `repeat(${this.columns > 0 && !this.screen.isMobile ? this.columns : 1}, 1fr)` })}'
        >
        ${(_e = filteredParticipants.map(publicListingFeedback)) !== null && _e !== void 0 ? _e : nothing}
      </ul>
      ${this.expandable && this.participants.length > this.nrOfFeedbacks ? showMoreButton : nothing}
    `;
    }
    toggleExpandable() {
        if (this.expanded) {
            this.expanded = false;
            trackEvent({
                name: 'Hide public feedback',
                intent: 'Close',
                type: 'Click',
                position: this.position
            }, 'Hide public feedback', 'Trust public listing', this.vertical);
        }
        else {
            this.expanded = true;
            trackEvent({
                name: 'Load more public feedback',
                intent: 'Open',
                type: 'Click',
                position: this.position
            }, 'Load more public feedback', 'Trust public listing', this.vertical);
        }
    }
};
__decorate([
    property()
], PublicListing.prototype, "profileUrl", void 0);
__decorate([
    property({ type: String })
], PublicListing.prototype, "user", void 0);
__decorate([
    property({ type: Number })
], PublicListing.prototype, "columns", void 0);
__decorate([
    property({ type: Number })
], PublicListing.prototype, "nrOfFeedbacks", void 0);
__decorate([
    property({ type: Boolean })
], PublicListing.prototype, "noShadows", void 0);
__decorate([
    property({ type: Boolean })
], PublicListing.prototype, "showReplies", void 0);
__decorate([
    property({ type: Boolean })
], PublicListing.prototype, "expandable", void 0);
__decorate([
    property({ type: Boolean })
], PublicListing.prototype, "showTransactionalBadge", void 0);
__decorate([
    state()
], PublicListing.prototype, "participants", void 0);
__decorate([
    state()
], PublicListing.prototype, "expanded", void 0);
__decorate([
    state()
], PublicListing.prototype, "animation", void 0);
PublicListing = __decorate([
    customElement('trust-public-listing')
], PublicListing);
export { PublicListing };
//# sourceMappingURL=PublicListing.js.map